import { Set } from 'immutable'
import { LANGUAGE_EN } from '../languages/constants'
import { filterRecords } from '../data'
import { MEDIA_ASSETS_STORE_KEY } from '.'
import {
  MEDIA_ASSETS_PROVIDER_BRIGHTCOVE_HLS,
  MEDIA_ASSETS_PROVIDER_BRIGHTCOVE_DYNAMIC_DELIVERY,
  MEDIA_ASSETS_PROVIDER_HLS,
} from './providers'
import {
  MEDIA_ASSETS_MEDIA_REFERENCE_TYPE_MEDIA_STREAM,
  isPlayableStreamReferenceType,
} from './reference-types'

/**
 * The file player providers that are allowed by the video player
 */
export const VIDEO_PLAYER_ALLOWED_PROVIDERS = Set([
  MEDIA_ASSETS_PROVIDER_BRIGHTCOVE_HLS,
  MEDIA_ASSETS_PROVIDER_BRIGHTCOVE_DYNAMIC_DELIVERY,
  MEDIA_ASSETS_PROVIDER_HLS,
])

/**
 * Get playable media asset prefering first null language then english
 * and finally sort alphabetically
 * @param {Object} options The options
 * @param {Map} options.storeKey The store key
 * @param {Map} options.data The redux data store
 * @param {Map} options.mediaId The media Id
 * @param {String|null} [options.language=null] The prefered language
 * @returns The media asset based on the prefere language
 */
export function getPlayableMediaAssetStream (options = {}) {
  const { storeKey = MEDIA_ASSETS_STORE_KEY, data, mediaId, language = null } = options
  if (!data) {
    throw new Error('The data option is required')
  }
  if (!mediaId) {
    throw new Error('The mediaId option is required')
  }
  // Map being used as a dictionary-like keyed list
  const records = filterRecords(
    {
      data,
      storeKey,
    },
    (v) => v.get('mediaId') === mediaId,
  )
  // First try and find legacy default media asset i.e. language === null or requested language
  const mediaAssetsRecords = filterMediaAssetsMediaStreams(records, language)
  // Return the best result
  return mediaAssetsRecords.first()
}

/**
 * Return the media assets filtered to the playbale streams in that language.
 * Includes `ms` and `ms_clip`; prefers `ms` when both exist (matches Brooklyn pickPlayableMediaStreamAsset).
 * @param {import('immutable').List|import('immutable').Set} mediaAssets The media assets list
 * @param {String} language The desired language
 */
export function filterMediaAssetsMediaStreams (mediaAssets, language) {
  return mediaAssets
    .filter(
      (v) =>
        isPlayableStreamReferenceType(v.get('mediaReferenceType')) &&
        VIDEO_PLAYER_ALLOWED_PROVIDERS.includes(v.get('provider')),
    )
    .sort((a, b) => {
      // Prefer full media stream (`ms`) over clip stream (`ms_clip`)
      const aIsMs = a.get('mediaReferenceType') === MEDIA_ASSETS_MEDIA_REFERENCE_TYPE_MEDIA_STREAM
      const bIsMs = b.get('mediaReferenceType') === MEDIA_ASSETS_MEDIA_REFERENCE_TYPE_MEDIA_STREAM
      if (aIsMs && !bIsMs) {
        return -1
      }
      if (!aIsMs && bIsMs) {
        return 1
      }
      // First prefer null, then language match (language can be null) and finally english
      if (a.get('language') === null) {
        return -1
      }
      if (b.get('language') === null) {
        return 1
      }
      if (a.get('language') === language) {
        return -1
      }
      if (b.get('language') === language) {
        return 1
      }
      if (a.get('language') === LANGUAGE_EN) {
        return -1
      }
      if (b.get('language') === LANGUAGE_EN) {
        return 1
      }
      // At this point we just return the orignal order
      return 0
    })
}
